import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {appPaths} from '@/config/appPaths';
import type {BusinessFee} from '@/types';
import * as formatDate from '@/utils/date';

import FeeSummary from './FeeSummary';
import {getBusinessIdTargets} from './updateFeeReducer';
type ExtraRowArg = BusinessFee;

/** The settled negotiation a negotiated fee came from, filtered to its business. */
const negotiationLink = (fee: BusinessFee) => {
  const [businessId] = (fee.tags ?? []).flatMap(getBusinessIdTargets);
  const params = new URLSearchParams({
    negotiationTab: 'COMPLETED',
    negotiationId: String(fee.meta?.negotiationId),
    ...(businessId ? {merchantId: businessId} : {}),
  });
  return `/${appPaths.merchants}/pricing-negotiations?${params.toString()}`;
};

const BusinessFeesTable = ({
  items,
  onFeeSaved,
  businessId,
  businessName,
  dataCy,
}: {
  items: BusinessFee[];
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
  /** Called after a fee is updated so the list can refresh in place. */
  onFeeSaved?: () => void | Promise<void>;
  /** When rendered inside an enterprise profile, scopes fee updates to it. */
  businessId?: string;
  businessName?: string;
  /** Test hook prefix; the table and its cells namespace under it. */
  dataCy?: string;
}) => {
  return (
    <Table
      containerDataCy={dataCy ? `${dataCy}-table` : undefined}
      className="overflow-hidden"
    >
      <Thead>
        <Tr>
          <Th className="w-20">&nbsp;</Th>
          <Th>Name</Th>
          <Th>Status</Th>
          <Th>Date</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <BusinessFeesRow
            key={index}
            item={item}
            index={index}
            onFeeSaved={onFeeSaved}
            businessId={businessId}
            businessName={businessName}
            dataCy={dataCy}
          />
        ))}
      </TBody>
    </Table>
  );
};

const BusinessFeesRow = ({
  item,
  index,
  onFeeSaved,
  businessId,
  businessName,
  dataCy,
}: {
  item: BusinessFee;
  index: number;
  onFeeSaved?: () => void | Promise<void>;
  businessId?: string;
  businessName?: string;
  dataCy?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  // The queue is closed to roles that cannot view negotiations.
  const canOpenNegotiation = hasAdminPermission(
    localStorage.getItem('admin-role') ?? undefined,
    AfriexAdminPermission.BusinessFeeNegotiationRead,
  );
  const negotiatedBadge = (
    <Badge colorScheme="blue" className="normal-case">
      Negotiated
    </Badge>
  );
  return (
    <>
      <Tr className={zebraRow} data-cy={dataCy ? `${dataCy}-row` : undefined}>
        <Td isNumeric>
          <Button
            dataCy={dataCy ? `${dataCy}-expand` : undefined}
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 dark:hover:bg-gray-700 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-name` : undefined}>
          <span className="flex items-center gap-2">
            {item?.name}
            {item?.meta?.negotiationId && canOpenNegotiation ? (
              <Link
                to={negotiationLink(item)}
                title="Open the pricing negotiation this price came from"
              >
                {negotiatedBadge}
              </Link>
            ) : null}
            {item?.meta?.negotiationId && !canOpenNegotiation
              ? negotiatedBadge
              : null}
          </span>
        </Td>
        <Td data-cy={dataCy ? `${dataCy}-status` : undefined}>
          {!item?.meta?.flags?.isDeactivated ? 'ACTIVE' : 'INACTIVE'}
        </Td>
        <Td data-cy={dataCy ? `${dataCy}-date` : undefined}>
          {formatDate.DDMonYYYY(String(item.updatedAt))}
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <FeeSummary
                item={item}
                onFeeSaved={onFeeSaved}
                businessId={businessId}
                businessName={businessName}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default BusinessFeesTable;
